import React, { useEffect, useState } from "react";
import { Grid, Stack, Checkbox, Tooltip, IconButton } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";

import { Select, Input, RequiredUserType, MultiSelect } from "../Common";

import { TranslateW } from "../../Utils/Languages/Translate";
import Enums from "../../enums";
import UserInfo from "../../UserInfo";
import { toolTipBlue } from "../../color_utils";
import { trakrefFontFamily } from "../../utils";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CustomCheckbox = ({ label, checked, onChange }) => (
    <FormControlLabel
        value="end"
        sx={{
            padding: "0px",
            margin: "0px",
        }}
        control={
            <Checkbox
                sx={{
                    paddingLeft: "0px",
                    "&.Mui-checked": {
                        color: "#008299",
                    },
                }}
                checked={checked}
                onChange={onChange}
            />
        }
        label={label}
        labelPlacement="end"
    />
);

const {
    SuperTenantAdmin,
    TenantAdministrator,
    RegionalAdministrator,
    TechnicianManager,
    Technician,
    ReadOnly
} = Enums.UserTypes;

const userTypeOptions = {
    [SuperTenantAdmin]: [{
        id: SuperTenantAdmin,
        name: 'Super Tenant Administrator'
    }, {
        id: TenantAdministrator,
        name: 'Tenant Administrator'
    }, {
        id: RegionalAdministrator,
        name: 'Regional Administrator'
    }, {
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [TenantAdministrator]: [{
        id: TenantAdministrator,
        name: 'Tenant Administrator'
    }, {
        id: RegionalAdministrator,
        name: 'Regional Administrator'
    }, {
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [RegionalAdministrator]: [{
        id: RegionalAdministrator,
        name: 'Regional Administrator'
    }, {
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [TechnicianManager]: [{
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [Technician]: [{
        id: Technician,
        name: 'Technician'
    }],
    [ReadOnly]: [{
        id: ReadOnly,
        name: 'ReadOnly'
    }],
}

const userTypeOptionsContractorLite = {
    [TenantAdministrator]: [{
        id: TenantAdministrator,
        name: 'Tenant Administrator'
    }, {
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [TechnicianManager]: [{
        id: TechnicianManager,
        name: 'Technician Manager'
    }, {
        id: Technician,
        name: 'Technician'
    }, {
        id: ReadOnly,
        name: 'ReadOnly'
    }],
    [Technician]: [{
        id: Technician,
        name: 'Technician'
    }],
}

const UserSettingsForm = ({
    userInfo,
    options,
    onHandleValueChanged,
    isFieldValid,
    setErrorExist,
}) => {

    const [userTypes, setUserTypes] = useState([]);
    const [isUserTypeEnable, setIsUserTypeEnable] = useState(false);
    const isTechnician = !isNaN(userInfo.userType) && Number(userInfo.userType) === Technician;
    const isRegionalAdmin = !isNaN(userInfo.userType) && Number(userInfo.userType) === RegionalAdministrator;
    const disabledUserTypes = !options.manageRegion ? [
        RegionalAdministrator,
    ] : [];

    useEffect(() => {
        let types = [];

        if (UserInfo.isSystemAdministrator())
            setIsUserTypeEnable(false);

        if (UserInfo.isNativeSystemOwner()) {
            if (UserInfo.userId === userInfo.id || UserInfo.isTechnician() || UserInfo.isReadOnly())
                setIsUserTypeEnable(true);

            if (UserInfo.groupId in userTypeOptions)
                types = userTypeOptions[UserInfo.groupId];
        }
        else if (UserInfo.isNativeServiceProvider()) {
            if (UserInfo.userId === userInfo.id || UserInfo.isSuperTenantAdmin() || UserInfo.isRegionalAdministrator() || UserInfo.isTechnician() || UserInfo.isReadOnly())
                setIsUserTypeEnable(true);
            if (UserInfo.groupId in userTypeOptionsContractorLite)
                types = userTypeOptionsContractorLite[UserInfo.groupId];
        }

        if (options.manageRegion.length <= 0) {
            types = types.filter(x => x.id != RegionalAdministrator);
        }

        const fallBackTypes = options.group.length ? options.group : userTypeOptions[UserInfo.groupId] || [];

        setUserTypes(types.length ? types : fallBackTypes);
    }, [userInfo.id]);

    return (
        <>
            <Grid container spacing={6}>
                <Grid item sx={6} md={6}>
                    <p className="inputfields-header-label">Settings</p>
                    <Stack spacing={3}>
                        <Select
                            disabled={isUserTypeEnable}
                            value={userInfo.userType}
                            label={TranslateW("Profile.UserType")}
                            options={userTypes}
                            disabledMenuItems={disabledUserTypes}
                            onChange={(e) => onHandleValueChanged("userType", e.target.value)}
                        />
                        <RequiredUserType
                            userType={userInfo.userType}
                            allowedUserType={[RegionalAdministrator]}
                        >
                            <MultiSelect
                                required={isRegionalAdmin}
                                value={userInfo.userInstanceAssociationIds}
                                label="Manages Region"
                                options={options.manageRegion}
                                error={isRegionalAdmin && isFieldValid(userInfo.userInstanceAssociationIds)}
                                onChange={(e) => {
                                    const {
                                        target: { value },
                                    } = e;
                                    onHandleValueChanged(
                                        "userInstanceAssociationIds",
                                        typeof value === 'string' ? value.split(',') : value);
                                }}
                            />
                        </RequiredUserType>
                        <RequiredUserType
                            userType={userInfo.userType}
                            allowedUserType={[
                                SuperTenantAdmin,
                                Technician,
                                TechnicianManager,
                                TenantAdministrator,
                            ]}
                        >
                            <Select
                                required={isTechnician}
                                error={isTechnician && isFieldValid(userInfo.certificationTypeID)}
                                value={userInfo.certificationTypeID}
                                label={TranslateW("Profile.TechnicianCertificationType")}
                                options={options.certificationType}
                                onChange={(e) =>
                                    onHandleValueChanged("certificationTypeID", e.target.value)
                                }
                            />
                            <Input
                                required={isTechnician}
                                error={isTechnician && isFieldValid(userInfo.certificationNumber)}
                                onKeyDown={(event) => {
                                    //prevent specific character for certificationNumber field
                                    if (event.key === '-') {
                                        event.preventDefault();
                                    }
                                }}
                                fullWidth
                                value={userInfo.certificationNumber}
                                label={TranslateW("Profile.TechnicianCertificationNumber")}
                                helperText={"Invalid Certification Number"}
                                onChange={(e) =>
                                    onHandleValueChanged("certificationNumber", e.target.value)
                                }
                            />
                            <Input
                                fullWidth
                                value={userInfo.technicianTruck}
                                label={TranslateW("Profile.TechnicianTruck")}
                                onChange={(e) =>
                                    onHandleValueChanged("technicianTruck", e.target.value)
                                }
                            />
                        </RequiredUserType>
                        <Select
                            value={userInfo.preferredLeakDetectionMethodID}
                            label={TranslateW("Profile.PreferredLeakDetectionMethod")}
                            options={options.leakDetectionMethod}
                            onChange={(e) =>
                                onHandleValueChanged(
                                    "preferredLeakDetectionMethodID",
                                    e.target.value
                                )
                            }
                        />
                    </Stack>
                </Grid>
                <Grid item xs={6} md={6}>
                    <Stack spacing={3} sx={{ marginTop: "30px" }}>
                        <Select
                            value={userInfo.language}
                            label={TranslateW("Profile.Language")}
                            options={options.language}
                            onChange={(e) => onHandleValueChanged("language", e.target.value)}
                        />
                        <Select
                            value={userInfo.notificationTypeId}
                            label={TranslateW("Profile.Notification")}
                            options={options.notificationType}
                            onChange={(e) =>
                                onHandleValueChanged("notificationTypeId", e.target.value)
                            }
                        />
                        <Select
                            value={userInfo.weightUnits}
                            label={TranslateW("Profile.Weightsin")}
                            options={options.weightUnit}
                            onChange={(e) =>
                                onHandleValueChanged("weightUnits", e.target.value)
                            }
                        />
                        <div>
                            <CustomCheckbox
                                onChange={(e) =>
                                    onHandleValueChanged("isReseller", e.target.checked)
                                }
                                checked={userInfo.isReseller}
                                label={TranslateW("Profile.IsResell")}
                            />
                            <CustomCheckbox
                                onChange={(e) =>
                                    onHandleValueChanged("hasOtherInstances", e.target.checked)
                                }
                                checked={userInfo.hasOtherInstances}
                                label={TranslateW("Profile.MultipleAccount")}
                            />
                            {
                                (
                                    userInfo.userType === Enums.UserTypes.Technician ||
                                    userInfo.userType === Enums.UserTypes.TechnicianManager ||
                                    userInfo.userType === Enums.UserTypes.RegionalAdministrator
                                ) && UserInfo.isNativeSystemOwner() && (
                                    <CustomCheckbox
                                        onChange={(e) =>
                                            onHandleValueChanged("requireAdminApproval", e.target.checked)
                                        }
                                        checked={userInfo.requireAdminApproval}
                                        label={
                                            <div>
                                                <span>{TranslateW("Profile.RequireAdminApproval")}</span>
                                                <Tooltip
                                                    arrow
                                                    title="Require work order approval for this user. When enabled, any work orders the selected user closes will require admin approval."
                                                    placement='right'
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: toolTipBlue,
                                                                fontSize: "12px",
                                                                fontFamily: trakrefFontFamily,
                                                                '& .MuiTooltip-arrow': {
                                                                    color: toolTipBlue,
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <IconButton sx={{
                                                        ":focus": {
                                                            outline: "none",
                                                        },
                                                        marginLeft: 1
                                                    }}>
                                                        <InfoOutlinedIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        }
                                    />
                                )
                            }
                        </div>
                    </Stack>
                </Grid>
            </Grid>
        </>
    );
};

export default UserSettingsForm;
